import React, { useState } from 'react';
import './KPIProductionResult.css';
import { 
  Box, 
  Typography, 
  IconButton, 
  Grid, 
  Card, 
  CardContent, 
  useTheme, 
  alpha, 
  Alert,
  Button,
  CircularProgress
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useDomain } from '../../../contexts/DomainContext';
import HelpModal from '../../Common/HelpModal';
import KPIEquipmentCard from './components/KPIEquipmentCard';
import { useKPIProductionResult } from './hooks/useKPIProductionResult';
import { useForm } from 'react-hook-form';
import { bottomInfoMessages, helpModalMessages, FORM_DEFAULT_VALUES } from './constants';

/**
 * KPI 생산실적등록 컴포넌트
 * 센서 연결된 설비별로 독립적인 생산실적 등록 화면
 *
 * @param {Object} props - 컴포넌트 속성
 * @returns {JSX.Element}
 */
const KPIProductionResult = (props) => {
  // 테마 설정
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const { domain } = useDomain();

  // react-hook-form 설정
  const {
    control,
    handleSubmit,
    reset,
    watch,
    getValues,
  } = useForm({
    defaultValues: FORM_DEFAULT_VALUES
  });

  // 초기화 핸들러
  const handleReset = () => {
    reset(FORM_DEFAULT_VALUES);
  };

  // 커스텀 훅 사용
  const {
    // KPI 설비 관련
    isLoading,
    kpiEquipments,
    errorMessage,
    handleSearch,
    handleSaveEquipmentResult,
    
    // 색상 및 테마
    getTextColor,
    getBgColor,
    getBorderColor,

    // 옵션 데이터
    productOptions,
    warehouseOptions,
    unitOptions,
    vendorOptions,

    // 리프레시 키
    refreshKey
  } = useKPIProductionResult();

  // 검색 이벤트 핸들러
  const onSearch = (data) => {
    // 검색 실행
    handleSearch(data);
  };

  return (
    <Box className="kpi-production-result-container" sx={{ p: 0, width: '100%', pl: 0 }}>
      <Box className="page-header" sx={{ borderBottom: `1px solid ${getBorderColor()}`, px: 0, pl: 0 }}>
        <Typography
          variant="h5"
          component="h2"
          className="page-title"
          sx={{
            fontWeight: 600,
            color: getTextColor(),
            pl: 0
          }}
        >
          KPI 생산실적등록
        </Typography>
        <IconButton
          onClick={() => setIsHelpModalOpen(true)}
          className="help-icon-button"
          sx={{
            color: isDarkMode ? theme.palette.primary.light : theme.palette.primary.main,
            '&:hover': {
              backgroundColor: isDarkMode
                ? alpha(theme.palette.primary.light, 0.1)
                : alpha(theme.palette.primary.main, 0.05)
            }
          }}
        >
          <HelpOutlineIcon />
        </IconButton>
      </Box>

      {/* 오류 메시지 표시 */}
      {errorMessage && (
        <Alert
          severity="error"
          className="error-alert"
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={handleSubmit(onSearch)}
            >
              다시 시도
            </Button>
          }
        >
          {errorMessage}
        </Alert>
      )}

      {/* 로딩 표시 */}
      {isLoading && (
        <Box className="loading-indicator-container">
          <CircularProgress />
        </Box>
      )}

      {/* KPI 설비별 등록 영역 */}
      {!isLoading && (
        <Box className="equipment-cards-container" sx={{ mt: 2, width: '100%', px: 0 }}>
          <Grid container spacing={2} sx={{ width: '100%', margin: 0 }}>
            {kpiEquipments.map((equipment) => (
              <Grid 
                item 
                xs={12} 
                sm={kpiEquipments.length === 1 ? 12 : 6}
                md={kpiEquipments.length === 1 ? 12 : Math.floor(12 / kpiEquipments.length)}
                lg={kpiEquipments.length === 1 ? 12 : Math.floor(12 / kpiEquipments.length)}
                xl={kpiEquipments.length === 1 ? 12 : Math.floor(12 / kpiEquipments.length)}
                key={equipment.equipmentId}
                sx={{ 
                  minWidth: kpiEquipments.length === 1 ? '100%' : 'auto',
                  flex: kpiEquipments.length === 1 ? '1 1 100%' : `1 1 ${100 / kpiEquipments.length}%`
                }}
              >
                <KPIEquipmentCard
                  equipment={equipment}
                  productOptions={productOptions}
                  warehouseOptions={warehouseOptions}
                  unitOptions={unitOptions}
                  vendorOptions={vendorOptions}
                  onSave={handleSaveEquipmentResult}
                  getTextColor={getTextColor}
                  getBgColor={getBgColor}
                  getBorderColor={getBorderColor}
                  isDarkMode={isDarkMode}
                />
              </Grid>
            ))}
          </Grid>

          {/* 설비가 없는 경우 메시지 */}
          {kpiEquipments.length === 0 && (
            <Box 
              sx={{ 
                textAlign: 'center', 
                py: 4,
                bgcolor: getBgColor(),
                borderRadius: 1,
                border: `1px solid ${getBorderColor()}`
              }}
            >
              <Typography variant="body1" color={getTextColor()}>
                조회 조건에 해당하는 KPI 설비가 없습니다.
              </Typography>
              <Typography variant="body2" color={getTextColor()} sx={{ mt: 1 }}>
                검색 조건을 변경하여 다시 조회해주세요.
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* 하단 정보 영역 */}
      <Box className="info-box" mt={2} p={2} sx={{
        bgcolor: getBgColor(),
        border: `1px solid ${getBorderColor()}`
      }}>
        <Box>
          {bottomInfoMessages.map((message, index) => (
            <Typography key={index} variant="body2" color={getTextColor()}>
              {message}
            </Typography>
          ))}
        </Box>
      </Box>

      {/* 도움말 모달 */}
      <HelpModal
        open={isHelpModalOpen}
        onClose={() => setIsHelpModalOpen(false)}
        title={helpModalMessages.title}
      >
        {helpModalMessages.paragraphs.map((paragraph, index) => (
          <Typography key={index} variant="body2" color={getTextColor()} paragraph>
            {paragraph}
          </Typography>
        ))}
      </HelpModal>
    </Box>
  );
};

export default KPIProductionResult;